import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import ActionPlanGrid from "../../components/ActionPlanGrid";
import { PageContent } from "../../components";
import { AppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import lwn_data from "../../data";
import { Box, Button } from "@mui/material";

const ActionPlan = () => {
  const { questionnaires, getRankings, allAnswered, changeCurrentStep, removeRankingAndIndexes } =
    useContext(AppContext);
  const [barriersData, setBarriersData] = useState([]);

  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const questionnaire = questionnaires.find((q) => q.questionnaireId === "0");
    if (questionnaire === undefined || questionnaire.answers === undefined) {
      navigate("/");
      return;
    }

    const answers = questionnaire.answers;
    const prioritizedAnswers = getRankings();
    // Ensure the structure of prioritizedAnswers is as required
    const formattedPrioritizedAnswers = prioritizedAnswers.map((item, index) => ({
      rank: index + 1,
      barrier: item.barrier.questionText,
      type: item.type,
    }));
    // Create a set of prioritized question texts to exclude from non-prioritized barriers
    const prioritizedTexts = new Set(formattedPrioritizedAnswers.map((answer) => answer.barrier));

    // Find identified barriers that are "Yes" and prioritized
    const identifiedPrioritizedBarriers = formattedPrioritizedAnswers.map((answer) => ({
      priority: answer.rank,
      barriers: answer.barrier,
      recommended: true,
      completed: false,
      type: answer.type,
    }));

    // Find identified barriers that are "Yes" but not prioritized
    const identifiedNotPrioritizedBarriers = answers
      .filter((answer) => answer.text === "0" && !prioritizedTexts.has(answer.questionText))
      .map((answer) => ({
        priority: null,
        barriers: answer.questionText,
        recommended: true,
        completed: false,
        type: answer.type,
      }));

    // Find barriers that are "No" or "1"
    const identifiedNotApplicableBarriers = answers
      .filter((answer) => answer.text === "1")
      .map((answer) => ({
        priority: null,
        barriers: answer.questionText,
        recommended: false, // Adjust as needed
        completed: true, // Assuming these are not actionable
        type: answer.type,
      }));

    // Find barriers that are "Unsure" or "2"
    const identifiedUnsureBarriers = answers
      .filter((answer) => answer.text === "2")
      .map((answer) => ({
        priority: null,
        barriers: answer.questionText,
        recommended: false, // Adjust as needed
        completed: false, // Assuming unsure requires further assessment
        type: answer.type,
      }));

    // Concatenate all identified barriers
    const allBarriers = [
      ...identifiedPrioritizedBarriers,
      ...identifiedNotPrioritizedBarriers,
      ...identifiedNotApplicableBarriers,
      ...identifiedUnsureBarriers,
    ];

    // Remove duplicates based on question text
    const uniqueBarriers = Array.from(
      new Map(allBarriers.map((item) => [item.barriers, item])).values()
    );

    // Add questionnaireId and completed status to each barrier
    const updatedData = uniqueBarriers.map((item) => {
      const matchedQuestionnaire = lwn_data.find((q) => q.questionnaireType === item.type);
      return {
        ...item,
        questionnaireId: matchedQuestionnaire ? matchedQuestionnaire.questionnaireId : null,
        completed: matchedQuestionnaire ? allAnswered(matchedQuestionnaire.questionnaireId) : false,
        questions: matchedQuestionnaire
          ? questionnaires
            .find((q) => q.questionnaireId === matchedQuestionnaire.questionnaireId)
            ?.answers.filter((answer) => answer.text === "0")
          : null,
      };
    });

    setBarriersData(updatedData);
  }, []);

  const handleBackClick = async () => {
    // await removeRankingAndIndexes();
    navigate("/BarrierQuestions/0");
    changeCurrentStep(1);
  };

  return (
    <PageContent title={t("title_action_plan")}>
      <ActionPlanGrid data={barriersData} />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackClick}
          sx={{
            marginRight: "13px",
          }}
        >
          {t("start_over")}
        </Button>
      </Box>
    </PageContent>
  );
};

export default ActionPlan;
